import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { formatApiErrorDetail } from "../lib/api";
import useSeo from "../hooks/useSeo";

export default function Login() {
    const { login } = useAuth();
    const nav = useNavigate();
    const loc = useLocation();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);

    useSeo({
        title: "Login | IDCardGen",
        description:
            "Login to IDCardGen to manage templates, credits, payments and bulk ID card exports.",
        canonical: "https://idcardgen.com/login",
    });

    const onSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const u = await login(email, password);
            toast.success(`Welcome back, ${u.name}`);
            const from = loc.state?.from;
            if (u.role === "admin") nav("/admin");
            else nav(from || "/dashboard");
        } catch (err) {
            toast.error(
                formatApiErrorDetail(err.response?.data?.detail) || err.message,
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            className="min-h-[calc(100vh-4rem)] grid md:grid-cols-2"
            data-testid="login-page"
        >
            <div className="hidden md:flex flex-col justify-between p-12 bg-zinc-950 text-white relative overflow-hidden">
                <div className="accent-strip absolute top-0 left-0 right-0 h-1" />
                <div>
                    <div className="text-xs font-mono uppercase tracking-[0.3em] text-zinc-400">
                        Welcome back
                    </div>
                    <h2 className="font-display font-black text-5xl tracking-tighter mt-3">
                        Login to your studio.
                    </h2>
                </div>
                <div className="relative">
                    <div className="text-6xl font-display font-black tracking-tighter text-zinc-800">
                        01
                    </div>
                    <div className="text-sm text-zinc-400 mt-2 max-w-sm">
                        Your templates, students and credits are waiting.
                        Continue where you left off.
                    </div>
                </div>
                <div className="text-xs font-mono text-zinc-500 uppercase tracking-widest">
                    IDCardGen · Secure Login
                </div>
            </div>
            <div className="flex items-center justify-center p-6 md:p-12">
                <form onSubmit={onSubmit} className="w-full max-w-sm space-y-5">
                    <div>
                        <h1 className="font-display font-black text-3xl tracking-tighter">
                            Sign in
                        </h1>
                        <p className="text-sm text-zinc-500 mt-1">
                            Use your registered email &amp; password.
                        </p>
                    </div>
                    <div>
                        <label className="label-swiss">Email</label>
                        <input
                            type="email"
                            className="input-swiss"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            data-testid="login-email-input"
                        />
                    </div>
                    <div>
                        <label className="label-swiss">Password</label>
                        <input
                            type="password"
                            className="input-swiss"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            data-testid="login-password-input"
                        />
                    </div>
                    <button
                        type="submit"
                        disabled={loading}
                        className="btn-primary w-full"
                        data-testid="login-submit-btn"
                    >
                        {loading ? "Signing in…" : "Sign in"}
                    </button>
                    <p className="text-sm text-zinc-500">
                        No account?{" "}
                        <Link
                            to="/register"
                            className="text-blue-600 font-medium"
                            data-testid="login-goto-register"
                        >
                            Register
                        </Link>
                    </p>
                </form>
            </div>
        </div>
    );
}
